// useWebBleKeepAlive — mantiene VIVA la conexión BLE de las impresoras con auto-print
// activo, para que la impresión "AL RECIBIR" (evento de socket en segundo plano, sin
// gesto del usuario) encuentre la impresora ya conectada.
//
// Problema que resuelve: las térmicas BLE se DUERMEN entre pedidos y la conexión se cae.
// La impresión manual / "AL ACEPTAR" funcionan porque ocurren durante una interacción del
// usuario (se reconecta en ese contexto). Pero "AL RECIBIR" llega en background: si la
// reconexión fría falla, no imprime. Aquí reconectamos proactivamente (al montar y en un
// intervalo) y, best-effort, escuchamos anuncios BLE (watchAdvertisements) para reconectar
// en cuanto la impresora despierte — el patrón del spike para "socket → auto-print".
//
// Es best-effort y silencioso: cualquier fallo de reconexión se ignora (no debe romper
// nada). Solo actúa si el navegador soporta Web Bluetooth y el master toggle está ON.

import { useEffect } from 'react';
import { useWebBlePrintersFromStore } from '@store/actions/webBlePrinters';
import { useWebBlePrintMode } from '@utils/print/webble/printMode';
import { getConnection, isWebBleSupported, reconnectKnown, watchAndReconnect } from '@utils/print/webble/bleTransport';
import type { WebBlePrintSettings } from '@store/slices/webBlePrinters';

const KEEPALIVE_INTERVAL_MS = 25000;

export const useWebBleKeepAlive = (): void => {
  const printers = useWebBlePrintersFromStore();
  const { enabled: blePrintModeOn } = useWebBlePrintMode();

  useEffect(() => {
    if (!isWebBleSupported() || !blePrintModeOn) return;

    // Todas las impresoras emparejadas y habilitadas: así, al RECARGAR la página, se
    // reconectan en silencio (vía getDevices) y la impresión —manual o auto— las encuentra
    // listas, sin el error "vuelve a emparejarla".
    const targets = Object.values(printers).filter(
      (p: WebBlePrintSettings) => p.enabled && Boolean(p.deviceId || p.deviceName),
    );
    if (targets.length === 0) return;

    let cancelled = false;
    // Keyed por deviceId (no array): el ciclo periódico re-llama warm y watchAndReconnect
    // devuelve un no-op cuando el device ya está observado — acumularlos en un array crecería
    // sin tope en sesiones largas (Seer). Una entrada por dispositivo, sin pisar la real.
    const unsubscribers = new Map<string, () => void>();

    const warm = async (p: WebBlePrintSettings) => {
      try {
        const conn = await reconnectKnown({
          deviceId: p.deviceId ?? undefined,
          deviceName: p.deviceName ?? undefined,
        });
        if (conn && !cancelled && !unsubscribers.has(conn.device.id)) {
          // Reconexión por anuncios cuando la impresora despierte (best-effort; degrada
          // en silencio si el navegador no lo soporta).
          unsubscribers.set(
            conn.device.id,
            watchAndReconnect(conn.device, () => undefined),
          );
        }
      } catch {
        /* best-effort: ignorar fallos de reconexión */
      }
    };

    targets.forEach(warm);

    // Re-chequeo periódico: si la conexión se cayó, reconectar.
    const interval = window.setInterval(() => {
      targets.forEach((p) => {
        const live = p.deviceId ? getConnection(p.deviceId) : undefined;
        if (!live?.device.gatt?.connected) warm(p);
      });
    }, KEEPALIVE_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [printers, blePrintModeOn]);
};
